import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PageHeader, QuickFilters, DataTable, Tag, Tooltip, Button, Sheet, Dialog, ConfirmDialog, Field, TextInput, Select, Checkbox,
  RadioList, Banner, EmptyState, Spinner, applyClientQuery, describeFilter, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import ImportSectionNext from '../../components/ImportSectionNext.jsx';
import { GemaraSelectionEditorNext } from '../../components/GemaraNext.jsx';
import { selectionToEntries, entriesToSelection, isGemaraEntriesValid } from '../../components/gemaraSelectionModel.js';
import MemberCardNext from './MemberCardNext.jsx';
import MessageBroadcastDialog from '../../components/MessageBroadcastDialog.jsx';
import { buildMemberGradesMap } from '../../utils/gradeSummary.js';
import MemberReportIssueDialog from './reports/MemberReportIssueDialog.jsx';
import MemberReportTemplateEditor from './reports/MemberReportTemplateEditor.jsx';
import { GROUP_BY_OPTIONS } from './reports/memberReportMergeFields.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import { useQuickCreate } from '../../next/quickCreate';
import { useScreenSettings } from '../../next/related/relatedSettingsStore';
import { AudioPlayer, MultiCheck, Row, apiError } from '../../next/ui';

/**
 * רשימת חברים — new design. Same data and endpoints as MembersList:
 * members + registrations + grades of the active (or last) period, quick filters (update requests / examined),
 * pinned search terms, inline occupation, member card (side sheet), mark examined, update requests, delete,
 * full Excel export, member reports + their design, tzintuk and message broadcast to selected / filtered / all, and the members import.
 * The table's query is controlled here, so reports, tzintuk and broadcast target exactly the rows the table shows.
 */
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SYSTEM_ID = String(process.env.REACT_APP_SYSTEM_ID || '').replace(/^["']|["']$/g, '').trim();
const IS_MISHTAMERET = SYSTEM_ID === 'waiting_client';
const PHONE_ALT_LABEL = IS_MISHTAMERET ? 'טלפון אשה' : 'טלפון נוסף';
const OCCUPATION_OPTIONS = IS_MISHTAMERET ? ['עמל הפרנסה', 'כולל', 'ישיבת היכל התורה'] : ['בעה"ב', 'אברך כולל'];

/** שם חבר — בצבע שונה כשיש פער נתונים שלא טופל (member.dataIssue), עם הסיבה ב-tooltip */
const DATA_ISSUE_COLOR = '#c62828';
function MemberName({ row, children }) {
  if (!row?.dataIssue) return <b>{children}</b>;
  return (
    <Tooltip title={row.dataIssue}>
      <b style={{ color: DATA_ISSUE_COLOR }}>{children}</b>
    </Tooltip>
  );
}
const FULL_EXPORT_GROUP_LABELS = Object.fromEntries(GROUP_BY_OPTIONS.map((o) => [o.value, o.label]));
const FIELD_LABEL = { firstName: 'שם פרטי', lastName: 'שם משפחה', fatherName: 'שם האב', fatherInLawName: 'שם השווער', city: 'ישוב', street: 'רחוב' };
const pendingOf = (row) => (row.pendingUpdates || []).filter((u) => u.status === 'pending');
const code4 = (v) => (v != null ? String(v).padStart(4, '0') : '');

export default function MembersListNext() {
  const toast = useCkToast();
  const { hasPermission } = useAuth();
  const canReadGrades = hasPermission('grades', 'read');
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quick, setQuick] = useState('all');
  const [pinnedSearches, setPinnedSearches] = useState([]);
  const [query, setQuery] = useState({ page: 1, pageSize: 50, search: '', sort: { key: 'lastName', dir: 1 }, filters: {} });
  const [selectedId, setSelectedId] = useState(null);
  useQuickCreate(() => setSelectedId('new'));
  const [selection, setSelection] = useState({ ids: [], count: 0, all: false });
  const [activePeriodName, setActivePeriodName] = useState('');
  const [examPeriods, setExamPeriods] = useState([]);
  const [pendingMember, setPendingMember] = useState(null);
  const [approveValues, setApproveValues] = useState({});
  const [resolveLoading, setResolveLoading] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [examine, setExamine] = useState(null); // { memberId, memberName, regs, changes }
  const [examineSaving, setExamineSaving] = useState(false);
  const [fullExportOpen, setFullExportOpen] = useState(false);
  const [fullExportLoading, setFullExportLoading] = useState(false);
  const [fx, setFx] = useState({ report: 'full', mode: 'single', groupBy: 'kolel', periodIds: [], filterPeriods: false });
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [reportTemplatesOpen, setReportTemplatesOpen] = useState(false);
  const [tzintuk, setTzintuk] = useState(null); // { target, preview, loading, sending, error, result }
  const [broadcastTarget, setBroadcastTarget] = useState(null); // { memberIds, label }
  const canBroadcast = IS_MISHTAMERET && hasPermission('members', 'update');

  useScreenSettings([
    { key: 'members-report-design', title: 'עיצוב ורקע לדוחות', description: 'תבניות הדוחות וכרטיסי הנבחן שמונפקים מרשימת החברים', icon: 'edit', onClick: () => setReportTemplatesOpen(true) },
  ]);

  /* ── data ── */
  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settle = (p) => p.then((r) => r).catch(() => null);
      const [membersRes, regsRes, periodsRes, gradesRes] = await Promise.all([
        api.get('/members', { params: { page: 1, pageSize: 100000 } }),
        settle(api.get('/registrations', { params: { page: 1, pageSize: 100000 } })),
        settle(api.get('/exam-periods')),
        canReadGrades ? settle(api.get('/grades', { params: { all: 1 } })) : Promise.resolve(null),
      ]);
      const allPeriods = periodsRes?.data?.data || [];
      setExamPeriods(allPeriods);
      const lastPeriod = allPeriods.find((p) => p.isActive) || allPeriods[allPeriods.length - 1] || null;
      const lastPeriodId = lastPeriod?._id || null;
      setActivePeriodName(lastPeriod?.name || '');
      const gradesMap = canReadGrades ? buildMemberGradesMap(gradesRes?.data?.data || [], lastPeriodId, { isGemara: IS_OPEN_GEMARA }) : {};
      const memberRegsMap = {};
      const regMap = {};
      const examinedMap = {};
      (regsRes?.data?.data || []).forEach((reg) => {
        if (reg.status !== 'registered') return;
        const regPeriodId = reg.periodId?._id ? String(reg.periodId._id) : String(reg.periodId || '');
        if (lastPeriodId && regPeriodId !== String(lastPeriodId)) return;
        const mid = String(typeof reg.memberId === 'object' ? reg.memberId?._id : reg.memberId);
        if (!mid || mid === 'undefined') return;
        (memberRegsMap[mid] = memberRegsMap[mid] || []).push(reg);
        const codePrefix = reg.examId?.code != null ? `[${reg.examId.code}] ` : '';
        const label = codePrefix + (reg.examId?.name || (reg.gemaraSelection?.length ? reg.gemaraSelection.map((s) => s.tractate).join(', ') : ''));
        if (label) regMap[mid] = regMap[mid] ? `${regMap[mid]}, ${label}` : label;
        if (reg.gemaraSelection?.length) {
          const t = reg.gemaraSelection.filter((s) => s.examined).map((s) => s.tractate);
          if (t.length) examinedMap[mid] = examinedMap[mid] ? `${examinedMap[mid]}, ${t.join(', ')}` : t.join(', ');
        } else if (reg.examined) {
          const ex = codePrefix + (reg.examId?.name || 'כן');
          examinedMap[mid] = examinedMap[mid] ? `${examinedMap[mid]}, ${ex}` : ex;
        }
      });
      setAllData((membersRes.data.data || []).map((m) => {
        const k = String(m._id);
        const g = gradesMap[k] || { summary: '', items: [] };
        return { ...m, _examRegistration: regMap[k] || '', _examined: examinedMap[k] || '', _regs: memberRegsMap[k] || [], _gradesSummary: g.summary, _grades: g.items };
      }));
    } catch (e) {
      setError(apiError(e, 'שגיאה בשליפת חברים'));
    } finally {
      setLoading(false);
    }
  }, [canReadGrades]);
  useEffect(() => { loadAllData(); }, [loadAllData]);

  /* ── inline occupation ── */
  const changeOccupation = useCallback(async (row, value) => {
    const next = value === 'בחר' ? '' : value;
    if (!row?._id || next === (row.occupation || '')) return;
    try {
      const { data } = await api.put(`/members/${row._id}`, { version: row.version, occupation: next });
      setAllData((prev) => prev.map((m) => (String(m._id) === String(row._id) ? { ...m, occupation: data?.occupation || '', version: data?.version ?? m.version } : m)));
      toast.success('העיסוק עודכן');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעדכון עיסוק'));
    }
  }, [toast]);

  /* ── data issue → handled (clears dataIssue; the reason stays in the audit log) ── */
  const markIssueHandled = useCallback(async (row) => {
    if (!row?._id || !row.dataIssue) return;
    if (!window.confirm(`לסמן כטופל את ${row.firstName || ''} ${row.lastName || ''}?\n\n${row.dataIssue}`)) return;
    try {
      const { data } = await api.put(`/members/${row._id}`, { version: row.version, dataIssue: '' });
      setAllData((prev) => prev.map((m) => (String(m._id) === String(row._id) ? { ...m, dataIssue: '', version: data?.version ?? m.version } : m)));
      toast.success('סומן כטופל');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בסימון כטופל'));
    }
  }, [toast]);

  /* ── columns ── */
  const columns = useMemo(() => [
    {
      key: '_examined',
      label: 'נבחן',
      type: 'enum',
      getValue: (r) => r._examined || '—',
      render: (r) => (r._examined ? <Tag tone="success" icon="check" title={r._examined}>{r._examined.length > 24 ? `${r._examined.slice(0, 24)}…` : r._examined}</Tag> : <span className="ck-muted">—</span>),
    },
    {
      key: '_gradesSummary',
      label: activePeriodName ? `ציונים (${activePeriodName})` : 'ציונים',
      width: 220,
      getValue: (r) => r._gradesSummary || '',
      render: (r) => (r._grades?.length ? (
        <Tooltip title={r._gradesSummary}>
          <span className="nx-item__tags">{r._grades.map((g, i) => <Tag key={i} tone={g.score >= 90 ? 'success' : 'neutral'}>{`${g.label}: ${g.score}`}</Tag>)}</span>
        </Tooltip>
      ) : <span className="ck-muted">—</span>),
    },
    { key: 'firstName', label: 'שם פרטי', render: (r) => <MemberName row={r}>{r.firstName}</MemberName> },
    { key: 'lastName', label: 'משפחה', render: (r) => <MemberName row={r}>{r.lastName}</MemberName> },
    { key: 'fatherName', label: 'אביו' },
    { key: 'fatherInLawName', label: 'חתן' },
    { key: 'phone', label: 'נייד', ltr: true, copy: true },
    { key: 'phoneAlt', label: PHONE_ALT_LABEL, ltr: true, copy: true },
    { key: 'idNumber', label: 'מ.ז.', ltr: true, copy: true },
    {
      key: 'occupation',
      label: 'עיסוק',
      type: 'enum',
      getValue: (r) => r.occupation || 'בחר',
      render: (r) => (r.occupation ? <Tag>{r.occupation}</Tag> : <span className="ck-muted">בחר</span>),
      editable: {
        options: (r) => ['בחר', ...OCCUPATION_OPTIONS, ...(r.occupation && !OCCUPATION_OPTIONS.includes(r.occupation) ? [r.occupation] : [])].map((o) => ({ value: o, label: o })),
        onSave: (row, v) => changeOccupation(row, v),
      },
    },
    ...(IS_OPEN_GEMARA ? [{ key: 'kolel', label: 'כולל', type: 'enum' }] : []),
    { key: 'city', label: 'ישוב', type: 'enum' },
    { key: 'street', label: 'רחוב' },
    { key: 'buildingNumber', label: 'מספר בניין', filterable: false },
    { key: 'entrance', label: 'כניסה', filterable: false },
    { key: 'apartment', label: 'דירה', filterable: false },
    { key: 'status', label: 'סטטוס', type: 'enum', getValue: (r) => (r.status === 'active' ? 'פעיל' : 'לא פעיל'), render: (r) => <Tag tone={r.status === 'active' ? 'primary' : 'neutral'}>{r.status === 'active' ? 'פעיל' : 'לא פעיל'}</Tag> },
    { key: 'personalCode', label: 'קוד אישי', type: 'number', ltr: true, getValue: (r) => r.personalCode, render: (r) => (r.personalCode != null ? <span className="nx-num">{code4(r.personalCode)}</span> : '—') },
    { key: '_examRegistration', label: 'רשום למבחן', getValue: (r) => r._examRegistration || '—' },
  ], [activePeriodName, changeOccupation]);

  /* ── rows: quick filter + pinned terms → table query (controlled) ── */
  const baseRows = useMemo(() => {
    let r = allData;
    if (quick === 'pending') r = r.filter((m) => pendingOf(m).length);
    if (quick === 'examined') r = r.filter((m) => !!m._examined);
    if (quick === 'inactive') r = r.filter((m) => m.status !== 'active');
    pinnedSearches.forEach((term) => { r = applyClientQuery(r, columns, { search: term, filters: {} }); });
    return r;
  }, [allData, quick, pinnedSearches, columns]);
  const reportRows = useMemo(() => applyClientQuery(baseRows, columns, query), [baseRows, columns, query]);
  const filteredMemberIds = useMemo(() => reportRows.map((r) => String(r._id)), [reportRows]);
  const reportFilterSummary = useMemo(() => {
    const parts = [];
    if (pinnedSearches.length) parts.push(`חיפוש: ${pinnedSearches.join(', ')}`);
    if (String(query.search || '').trim()) parts.push(`חיפוש: ${String(query.search).trim()}`);
    if (quick === 'pending') parts.push('בקשות עדכון בלבד');
    if (quick === 'examined') parts.push('נבחנים בלבד');
    if (quick === 'inactive') parts.push('לא פעילים בלבד');
    if (activePeriodName) parts.push(`מועד: ${activePeriodName}`);
    Object.entries(query.filters || {}).forEach(([k, f]) => { const col = columns.find((c) => c.key === k); if (col && f) parts.push(describeFilter(col, f, new Date())); });
    return parts;
  }, [pinnedSearches, query, quick, activePeriodName, columns]);
  const selectedIds = selection.all ? filteredMemberIds : (selection.ids || []).map(String);

  const counts = useMemo(() => ({
    all: allData.length,
    pending: allData.filter((m) => pendingOf(m).length).length,
    examined: allData.filter((m) => !!m._examined).length,
    inactive: allData.filter((m) => m.status !== 'active').length,
  }), [allData]);

  const pinSearch = () => {
    const v = String(query.search || '').trim();
    if (!v || pinnedSearches.includes(v)) return;
    setPinnedSearches((p) => [...p, v]);
    setQuery((q) => ({ ...q, search: '', page: 1 }));
  };

  /* ── update requests ── */
  const resolve = async (member, item, action) => {
    const value = approveValues[item._id];
    setResolveLoading((p) => ({ ...p, [item._id]: true }));
    try {
      await api.post(`/members/${member._id}/pending-updates/${item._id}/resolve`, { action, ...(action === 'approve' ? { value } : {}) });
      const patch = (m) => ({ ...m, pendingUpdates: (m.pendingUpdates || []).map((u) => (String(u._id) === String(item._id) ? { ...u, status: action === 'approve' ? 'approved' : 'rejected' } : u)) });
      setAllData((prev) => prev.map((m) => (String(m._id) === String(member._id) ? patch(m) : m)));
      setPendingMember((m) => (m ? patch(m) : m));
      toast.success(action === 'approve' ? 'הבקשה אושרה והפרטים עודכנו' : 'הבקשה נדחתה');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בעיבוד הבקשה'));
    } finally {
      setResolveLoading((p) => ({ ...p, [item._id]: false }));
    }
  };

  /* ── delete ── */
  const deleteMember = async () => {
    try {
      await api.delete(`/members/${deleteTarget._id}`);
      setAllData((prev) => prev.filter((m) => m._id !== deleteTarget._id));
      toast.success('החבר נמחק');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקת חבר'));
    } finally {
      setDeleteTarget(null);
    }
  };

  /* ── mark examined (per member, current period) ── */
  const openExamine = (row) => {
    const regs = row._regs || [];
    const changes = {};
    regs.forEach((r) => { changes[r._id] = IS_OPEN_GEMARA ? selectionToEntries(r.gemaraSelection) : (r.examined || false); });
    setExamine({ memberId: row._id, memberName: `${row.firstName} ${row.lastName}`, regs, changes });
  };
  const examineValid = !IS_OPEN_GEMARA || (examine?.regs || []).every((reg) => isGemaraEntriesValid(examine.changes[reg._id]));
  const saveExamine = async () => {
    if (!examine || !examineValid) return;
    setExamineSaving(true);
    try {
      const saved = {};
      for (const reg of examine.regs) { // eslint-disable-line no-restricted-syntax
        const change = examine.changes[reg._id];
        if (IS_OPEN_GEMARA) {
          const { data: updated } = await api.put(`/registrations/${reg._id}`, { gemaraSelection: entriesToSelection(change) }); // eslint-disable-line no-await-in-loop
          saved[reg._id] = updated.gemaraSelection;
        } else if (change !== undefined && change !== (reg.examined || false)) {
          await api.put(`/registrations/${reg._id}`, { examined: change }); // eslint-disable-line no-await-in-loop
          saved[reg._id] = change;
        }
      }
      setAllData((prev) => prev.map((m) => {
        if (String(m._id) !== String(examine.memberId)) return m;
        const regs = (m._regs || []).map((r) => (IS_OPEN_GEMARA ? (saved[r._id] ? { ...r, gemaraSelection: saved[r._id] } : r) : { ...r, examined: examine.changes[r._id] !== undefined ? examine.changes[r._id] : r.examined }));
        const examined = IS_OPEN_GEMARA
          ? regs.flatMap((r) => (r.gemaraSelection || []).filter((s) => s.examined).map((s) => s.tractate)).join(', ')
          : (regs.some((r) => r.examined) ? 'כן' : '');
        return { ...m, _regs: regs, _examined: examined };
      }));
      toast.success('סטטוס בחינה עודכן');
      setExamine(null);
    } catch {
      toast.error('שגיאה בשמירת סטטוס בחינה');
    } finally {
      setExamineSaving(false);
    }
  };

  /* ── full export ── */
  const openFullExport = () => {
    const sorted = [...examPeriods].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const active = sorted.find((p) => p.isActive) || sorted[sorted.length - 1] || null;
    setFx({ report: 'full', mode: 'single', groupBy: 'kolel', filterPeriods: false, periodIds: active?._id ? [String(active._id)] : [] });
    setFullExportOpen(true);
  };
  const needsPeriods = fx.report === 'kolels-material' || fx.report === 'members-material' || (fx.report === 'full' && fx.filterPeriods);
  const runFullExport = async () => {
    if (needsPeriods && !fx.periodIds.length) { toast.error('יש לבחור לפחות מועד אחד'); return; }
    if (fx.report === 'full' && fx.mode === 'byKey' && !fx.groupBy) { toast.error('יש לבחור מפתח לפיצול'); return; }
    setFullExportLoading(true);
    try {
      const day = new Date().toISOString().slice(0, 10);
      const isZip = fx.mode === 'byKey' && (fx.report === 'full' || fx.report === 'members-material');
      const params = { report: fx.report };
      let filename;
      if (fx.report === 'kolels-material') {
        Object.assign(params, { periodIds: fx.periodIds.join(','), mode: 'single' });
        filename = `כוללים_חומר_${day}.xlsx`;
      } else if (fx.report === 'members-material') {
        Object.assign(params, { periodIds: fx.periodIds.join(','), mode: isZip ? 'byKey' : 'single', groupBy: 'kolel' });
        filename = isZip ? `חברים_חומר_${day}.zip` : `חברים_חומר_${day}.xlsx`;
      } else {
        params.mode = isZip ? 'byKey' : 'single';
        if (isZip) params.groupBy = fx.groupBy;
        if (fx.filterPeriods && fx.periodIds.length) params.periodIds = fx.periodIds.join(',');
        const g = FULL_EXPORT_GROUP_LABELS[fx.groupBy] || fx.groupBy;
        filename = isZip ? `חברים_מלא_לפי_${g}_${day}.zip` : `חברים_מלא_${day}.xlsx`;
      }
      const response = await api.get('/members/export-excel', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: isZip ? 'application/zip' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setFullExportOpen(false);
    } catch (err) {
      let message = 'שגיאה בייצוא אקסל';
      try { const t = await err.response?.data?.text?.(); if (t) message = JSON.parse(t)?.error || message; } catch { /* ignore */ }
      toast.error(message);
    } finally {
      setFullExportLoading(false);
    }
  };

  /* ── tzintuk: selected rows → table filter → all active ── */
  const sendTarget = () => {
    if (selectedIds.length) return { memberIds: selectedIds, label: `${selectedIds.length} שורות שנבחרו` };
    if (reportRows.length < allData.length) return { memberIds: filteredMemberIds, label: `${reportRows.length} חברים לפי הסינון` };
    return { memberIds: undefined, label: 'כל החברים הפעילים' };
  };
  const openTzintuk = async () => {
    const target = sendTarget();
    setTzintuk({ target, loading: true, preview: null, error: '', result: null, sending: false });
    try {
      const { data } = await api.post('/members/tzintuk/preview', { memberIds: target.memberIds });
      setTzintuk((t) => ({ ...t, loading: false, preview: data }));
    } catch (e) {
      setTzintuk((t) => ({ ...t, loading: false, error: apiError(e, 'שגיאה בבדיקת יתרת יחידות') }));
    }
  };
  const sendTzintuk = async () => {
    setTzintuk((t) => ({ ...t, sending: true, error: '' }));
    try {
      const { data } = await api.post('/members/tzintuk', { memberIds: tzintuk?.target?.memberIds });
      setTzintuk((t) => ({ ...t, sending: false, result: data }));
    } catch (e) {
      setTzintuk((t) => ({ ...t, sending: false, error: apiError(e, 'שגיאה בשליחת צינתוק') }));
    }
  };

  const pendingItems = pendingMember ? pendingOf(pendingMember) : [];
  const tz = tzintuk || {};

  return (
    <>
      <PageHeader
        title="רשימת חברים"
        sub={[activePeriodName ? `ציונים למועד: ${activePeriodName}` : null, `${reportRows.length.toLocaleString('he-IL')} מתוך ${allData.length.toLocaleString('he-IL')} חברים`].filter(Boolean).join(' · ')}
        primary={{ label: 'חבר חדש', onClick: () => setSelectedId('new') }}
        menus={[{
          key: 'reports',
          label: 'ייצוא ודוחות',
          icon: 'download',
          items: [
            { key: 'full-export', label: 'ייצוא אקסל מלא', icon: 'download', onClick: openFullExport, disabled: fullExportLoading },
            { key: 'report', label: 'הנפקת דוח', icon: 'file', onClick: () => setReportIssueOpen(true) },
            { key: 'report-design', label: 'התאם עיצוב ורקע', icon: 'edit', onClick: () => setReportTemplatesOpen(true) },
          ],
        }]}
        more={[
          canBroadcast ? { key: 'broadcast', label: 'שיגור הודעה', icon: 'mic', onClick: () => setBroadcastTarget(sendTarget()) } : null,
          { key: 'tzintuk', label: 'שליחת צינתוק', icon: 'phone', onClick: openTzintuk },
          { key: 'refresh', label: 'רענון', icon: 'refresh', onClick: loadAllData },
        ].filter(Boolean)}
      />

      <QuickFilters
        value={quick}
        onChange={(k) => { setQuick((c) => (c === k ? 'all' : k)); setQuery((q) => ({ ...q, page: 1 })); }}
        loading={loading && !allData.length}
        items={[
          { key: 'all', label: 'כל החברים', value: counts.all, icon: 'users', tone: 'neutral' },
          { key: 'examined', label: 'נבחנו במועד הנוכחי', value: counts.examined, icon: 'check', tone: 'success' },
          { key: 'pending', label: 'בקשות עדכון', value: counts.pending, icon: 'mic', tone: 'warning', warn: counts.pending > 0 },
          { key: 'inactive', label: 'לא פעילים', value: counts.inactive, icon: 'pause', tone: 'neutral' },
        ]}
      />

      <DataTable
        mode="client"
        rows={baseRows}
        rowKey="_id"
        columns={columns}
        loading={loading}
        error={error}
        onRetry={loadAllData}
        onRefresh={loadAllData}
        query={query}
        onQueryChange={setQuery}
        storageKey="bchinot-members"
        layoutDefaults={{ pinned: ['lastName'], hidden: ['fatherName', 'fatherInLawName', 'phoneAlt', 'street', 'buildingNumber', 'entrance', 'apartment', '_examRegistration'] }}
        searchPlaceholder="חיפוש חופשי בכל השדות… (Enter לנעיצה)"
        toolbarEnd={<Button size="sm" variant="ghost" icon="pin" disabled={!String(query.search || '').trim()} onClick={pinSearch} title="נעיצת מונח החיפוש">נעץ</Button>}
        extraChips={pinnedSearches.map((p) => ({ key: `pin-${p}`, label: `📌 ${p}`, icon: 'search', onRemove: () => setPinnedSearches((x) => x.filter((v) => v !== p)) }))}
        onClearAll={() => { setPinnedSearches([]); setQuick('all'); }}
        onSelectionChange={setSelection}
        exportFilename="חברים"
        emptyTitle="לא נמצאו חברים"
        onRowClick={(r) => setSelectedId(r._id)}
        actionsWidth={140}
        rowActions={(r) => [
          { key: 'edit', icon: 'edit', label: 'פתיחת כרטיס', onClick: () => setSelectedId(r._id) },
          (r._regs || []).length ? { key: 'examine', icon: 'check', label: 'סמן נבחן', onClick: () => openExamine(r) } : null,
          pendingOf(r).length ? { key: 'pending', icon: 'mic', label: `${pendingOf(r).length} בקשות עדכון ממתינות`, onClick: () => { setPendingMember(r); setApproveValues({}); } } : null,
          r.dataIssue ? { key: 'issue-handled', icon: 'check', label: `סמן כטופל — ${r.dataIssue}`, onClick: () => markIssueHandled(r) } : null,
        ].filter(Boolean)}
        rowMenu={(r) => [
          { key: 'edit', label: 'פתיחת כרטיס', icon: 'edit', onClick: () => setSelectedId(r._id) },
          (r._regs || []).length ? { key: 'examine', label: 'סמן נבחן', icon: 'check', onClick: () => openExamine(r) } : null,
          pendingOf(r).length ? { key: 'pending', label: 'בקשות עדכון', icon: 'mic', onClick: () => { setPendingMember(r); setApproveValues({}); } } : null,
          r.dataIssue ? { key: 'issue-handled', label: 'סמן פער נתונים כטופל', icon: 'check', onClick: () => markIssueHandled(r) } : null,
          { divider: true },
          { key: 'del', label: 'מחיקת חבר', icon: 'trash', danger: true, onClick: () => setDeleteTarget(r) },
        ].filter(Boolean)}
        bulkActions={[
          { key: 'report', label: 'הנפקת דוח', icon: 'file', onClick: () => setReportIssueOpen(true) },
          canBroadcast ? { key: 'broadcast', label: 'שיגור הודעה', icon: 'mic', onClick: () => setBroadcastTarget(sendTarget()) } : null,
          { key: 'tzintuk', label: 'צינתוק', icon: 'phone', onClick: openTzintuk },
        ].filter(Boolean)}
        renderMobileRow={(r) => (
          <div className="nx-item__main">
            <MemberName row={r}>{`${r.firstName || ''} ${r.lastName || ''}`}</MemberName>
            <small>{[r.phone, r.city, r.personalCode != null ? `קוד ${code4(r.personalCode)}` : null].filter(Boolean).join(' · ')}</small>
          </div>
        )}
      />

      <ImportSectionNext
        label="חברים"
        endpoint="/import/members"
        templateEndpoint="/import/template/members"
        description={'עמודות: שם פרטי, משפחה, אביו, חתן, נייד, כתובת (ישוב/רחוב/מספר/כניסה/דירה), וקוד כולל לפי סדר (לפנה"צ / אחה"צ / ערב).'}
        onImportDone={() => loadAllData()}
        entityType="members"
        identifierColumns={[{ field: 'phone', label: 'נייד (טלפון)' }, { field: 'idNumber', label: 'מ.ז. (תעודת זהות)' }, { field: 'personalCode', label: 'קוד אישי' }]}
      />

      <MemberReportIssueDialog
        open={reportIssueOpen}
        onClose={() => setReportIssueOpen(false)}
        filteredMemberIds={filteredMemberIds}
        filterSummary={reportFilterSummary}
        filteredCount={reportRows.length}
        selectedIds={selectedIds}
        periods={examPeriods}
      />
      <MemberReportTemplateEditor open={reportTemplatesOpen} onClose={() => setReportTemplatesOpen(false)} initialTargetType="list" />

      <Sheet open={!!selectedId} onClose={() => setSelectedId(null)} width={1080} ariaLabel="כרטיס חבר">
        {selectedId ? (
          <div className="nx-sheet-body">
            <MemberCardNext key={selectedId} recordId={selectedId} onClose={(reason) => { setSelectedId(null); if (reason === 'created') loadAllData(); }} />
          </div>
        ) : null}
      </Sheet>

      {/* update requests */}
      <Dialog
        open={!!pendingMember}
        onClose={() => setPendingMember(null)}
        title={`בקשות עדכון — ${pendingMember?.firstName || ''} ${pendingMember?.lastName || ''}`}
        icon="mic"
        size="md"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setPendingMember(null)}>סגירה</Button>
            <Button icon="external" onClick={() => { const id = pendingMember?._id; setPendingMember(null); setSelectedId(id); }}>פתח כרטיס מלא</Button>
          </>
        )}
      >
        {pendingItems.length === 0 ? <EmptyState compact icon="check" title="אין בקשות ממתינות" /> : (
          <div className="nx-list">
            {pendingItems.map((item) => (
              <div key={item._id} className="nx-item" style={{ alignItems: 'flex-start' }}>
                <div className="nx-stack" style={{ flex: 1, gap: 8 }}>
                  <Row justify="space-between">
                    <div className="nx-item__main"><b>{FIELD_LABEL[item.field] || item.label || item.field}</b><small>{item.requestedAt ? formatHebrewDateTime(item.requestedAt) : ''}</small></div>
                    {item.recordingUrl ? <AudioPlayer path={item.recordingUrl} label="הקלטת הבקשה" /> : <span className="ck-muted">אין הקלטה</span>}
                  </Row>
                  <Field label="ערך חדש (הקלידו לשמירה)">
                    <TextInput value={approveValues[item._id] || ''} placeholder={`לדוגמא: ${FIELD_LABEL[item.field] || item.field} החדש`} onChange={(e) => setApproveValues((p) => ({ ...p, [item._id]: e.target.value }))} />
                  </Field>
                  <Row>
                    <Button size="sm" variant="primary" icon="check" disabled={!approveValues[item._id]?.trim() || resolveLoading[item._id]} onClick={() => resolve(pendingMember, item, 'approve')}>{resolveLoading[item._id] ? 'שומר…' : 'אשר ועדכן'}</Button>
                    <Button size="sm" variant="danger" icon="x" disabled={resolveLoading[item._id]} onClick={() => resolve(pendingMember, item, 'reject')}>דחה</Button>
                  </Row>
                </div>
              </div>
            ))}
          </div>
        )}
      </Dialog>

      <MessageBroadcastDialog target={broadcastTarget} onClose={() => setBroadcastTarget(null)} />

      {/* tzintuk */}
      <Dialog
        open={!!tzintuk}
        onClose={() => !tz.sending && setTzintuk(null)}
        title="שליחת צינתוק"
        icon="phone"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={tz.sending} onClick={() => setTzintuk(null)}>{tz.result ? 'סגירה' : 'ביטול'}</Button>
            {!tz.result ? (
              <Button variant="primary" icon="phone" disabled={tz.sending || tz.loading || !tz.preview?.enough || !tz.preview?.count} onClick={sendTzintuk}>
                {tz.sending ? 'שולח…' : `שלח צינתוק ל־${tz.preview?.count ?? 0}`}
              </Button>
            ) : null}
          </>
        )}
      >
        <div className="nx-stack">
          {tz.loading ? <div className="ck-empty"><Spinner size={24} /></div> : tz.result && !tz.result.sent ? (
            <Banner tone="danger" icon="alert">{`הצינתוק לא נשלח${tz.result.errors?.length ? `: ${tz.result.errors[0]}` : ''}`}</Banner>
          ) : tz.result ? (
            <Banner tone={tz.result.failed ? 'warning' : 'success'} icon="check">
              {`נשלח צינתוק ל־${tz.result.sent} מספרים${tz.result.failed ? `, נכשלו ${tz.result.failed}` : ''}${tz.result.errors?.length ? ` (${tz.result.errors[0]})` : ''}`}
            </Banner>
          ) : tz.preview ? (
            <>
              <dl className="nx-kv">
                <div className="nx-kv__row"><dt>נמענים</dt><dd><b>{tz.target?.label}</b></dd></div>
                <div className="nx-kv__row"><dt>עם נייד תקין</dt><dd><b>{tz.preview.count}</b>{tz.preview.memberCount > tz.preview.count ? ` (מתוך ${tz.preview.memberCount})` : ''}</dd></div>
                <div className="nx-kv__row"><dt>עלות</dt><dd><b>{tz.preview.cost}</b> יחידות (0.1 למספר)</dd></div>
                <div className="nx-kv__row"><dt>יתרת היחידות</dt><dd><b>{Number(tz.preview.units).toFixed(1)}</b></dd></div>
              </dl>
              <p className="nx-desc">הצינתוק נשלח לנייד הבעל בלבד, בלי הודעה.</p>
              {!tz.preview.enough ? <Banner tone="danger" icon="alert">אין מספיק יחידות לשליחת הצינתוק. לא יישלח.</Banner> : null}
              {tz.preview.count === 0 ? <Banner tone="info" icon="info">אין חברים עם נייד תקין.</Banner> : null}
            </>
          ) : null}
          {tz.error ? <Banner tone="danger" icon="alert">{tz.error}</Banner> : null}
        </div>
      </Dialog>

      {/* delete */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteMember}
        title="מחיקת חבר לצמיתות"
        message={(
          <div className="nx-stack" style={{ gap: 8 }}>
            <Banner tone="warning" icon="alert">שימו לב: פעולה זו אינה ניתנת לביטול!</Banner>
            <p className="ck-confirm__msg">{`למחוק לצמיתות את החבר ${deleteTarget?.firstName || ''} ${deleteTarget?.lastName || ''}${deleteTarget?.phone ? ` (${deleteTarget.phone})` : ''}? כל הנתונים של החבר יימחקו ולא ניתן יהיה לשחזרם.`}</p>
          </div>
        )}
        confirmLabel="מחק לצמיתות"
        danger
        typeToConfirm="מחק"
      />

      {/* mark examined */}
      <Dialog
        open={!!examine}
        onClose={() => !examineSaving && setExamine(null)}
        title={`סמן נבחן — ${examine?.memberName || ''}`}
        icon="check"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={examineSaving} onClick={() => setExamine(null)}>ביטול</Button>
            <Button variant="primary" icon="check" disabled={examineSaving || !examine?.regs.length || !examineValid} onClick={saveExamine}>{examineSaving ? 'שומר…' : 'שמירה'}</Button>
          </>
        )}
      >
        {!examine?.regs.length ? <EmptyState compact icon="task" title="אין הרשמות פעילות למועד הנוכחי" /> : IS_OPEN_GEMARA ? (
          <div className="nx-stack">
            <p className="nx-desc">ערוך את טווחי הדפים וסמן את המסכתות שהנרשם נבחן בהן בפועל:</p>
            {examine.regs.map((reg) => (
              <div key={reg._id} className="nx-stack" style={{ gap: 6 }}>
                {examine.regs.length > 1 ? <h3 className="nx-sub-h">{`הרשמה${reg.periodId?.name ? ` — ${reg.periodId.name}` : ''}`}</h3> : null}
                <GemaraSelectionEditorNext value={examine.changes[reg._id] || []} onChange={(next) => setExamine((x) => ({ ...x, changes: { ...x.changes, [reg._id]: next } }))} showExamined />
              </div>
            ))}
          </div>
        ) : (
          <div className="nx-list">
            {examine.regs.map((reg) => (
              <div key={reg._id} className={`nx-item ${examine.changes[reg._id] ? 'is-on' : ''}`}>
                <Checkbox
                  checked={!!examine.changes[reg._id]}
                  onChange={(v) => setExamine((x) => ({ ...x, changes: { ...x.changes, [reg._id]: v } }))}
                  label={<span><b>{reg.examId?.name || '—'}</b>{reg.periodId?.name ? <span className="ck-muted">{` (${reg.periodId.name})`}</span> : null}</span>}
                />
              </div>
            ))}
          </div>
        )}
      </Dialog>

      {/* full export */}
      <Dialog
        open={fullExportOpen}
        onClose={() => !fullExportLoading && setFullExportOpen(false)}
        title="ייצוא אקסל מלא"
        icon="download"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={fullExportLoading} onClick={() => setFullExportOpen(false)}>ביטול</Button>
            <Button variant="primary" icon="download" disabled={fullExportLoading || (fx.report === 'full' && fx.mode === 'byKey' && !fx.groupBy) || (needsPeriods && !fx.periodIds.length)} onClick={runFullExport}>{fullExportLoading ? 'מייצא…' : 'ייצוא'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <Field label="סוג ייצוא">
            <RadioList
              value={fx.report}
              disabled={fullExportLoading}
              onChange={(v) => setFx((f) => ({ ...f, report: v, mode: v === 'kolels-material' ? 'single' : f.mode, groupBy: v === 'members-material' ? 'kolel' : f.groupBy }))}
              options={[
                { value: 'full', label: 'רשומה מלאה — חברים עם כל ההרשמות והציונים' },
                { value: 'kolels-material', label: 'רשימת כוללים עם חומר נבחן' },
                { value: 'members-material', label: 'רשימת חברים עם חומר נבחן' },
              ]}
            />
          </Field>
          {fx.report !== 'kolels-material' ? (
            <Field label="מצב ייצוא">
              <RadioList
                value={fx.mode}
                disabled={fullExportLoading}
                onChange={(v) => setFx((f) => ({ ...f, mode: v }))}
                options={[
                  { value: 'single', label: 'ייצוא רגיל — קובץ אחד' },
                  { value: 'byKey', label: fx.report === 'members-material' ? 'ייצוא לפי כולל — ZIP (אקסל לכל כולל)' : 'ייצוא לפי מפתח — ZIP (אקסל לכל ערך)' },
                ]}
              />
            </Field>
          ) : null}
          {fx.report === 'full' && fx.mode === 'byKey' ? (
            <Field label="מפתח לפיצול"><Select value={fx.groupBy} disabled={fullExportLoading} onChange={(v) => setFx((f) => ({ ...f, groupBy: v }))} options={GROUP_BY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} /></Field>
          ) : null}
          {fx.report === 'full' ? (
            <Checkbox checked={fx.filterPeriods} disabled={fullExportLoading} onChange={(v) => setFx((f) => ({ ...f, filterPeriods: v }))} label="סנן לפי מועדים (הרשמות וציונים)" />
          ) : null}
          {needsPeriods ? (
            <Field label="מועדים">
              <MultiCheck value={fx.periodIds} onChange={(v) => setFx((f) => ({ ...f, periodIds: v }))} options={examPeriods.map((p) => ({ value: String(p._id), label: `${p.name}${p.isActive ? ' (פעיל)' : ''}` }))} maxHeight={160} />
            </Field>
          ) : null}
          <Banner tone="info" icon="info">
            {fx.report === 'kolels-material' && 'קובץ אקסל: קוד/שם כולל, מועד, קוד/שם מבחן וחומר נבחן (נושא + סימנים).'}
            {fx.report === 'members-material' && (fx.mode === 'byKey' ? 'ZIP לפי כולל — לכל כולל אקסל עם חבריו והחומר שנבחנים עליו במועדים שנבחרו.' : 'אקסל עם חברים, מבחנים וחומר נבחן למועדים שנבחרו.')}
            {fx.report === 'full' && (fx.mode === 'byKey' ? 'ZIP עם אקסל מלא (חברים, הרשמות, ציונים) לכל ערך של המפתח.' : 'אקסל אחד עם כל החברים, ההרשמות והציונים (כולל חומר נבחן).')}
          </Banner>
        </div>
      </Dialog>
    </>
  );
}

